import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Zap, Shield, Globe, Check } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1508791290064-c27cc1ef7a9a?w=1600&q=85&auto=format&fit=crop';
const SERVICES_IMG = 'https://images.unsplash.com/photo-1680355065203-43ad84bb6e69?w=1400&q=85&auto=format&fit=crop';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { number: '57M+', label: 'Carbon Credits Issued' },
    { number: '250+', label: 'Clients' },
    { number: '400+', label: 'Active Projects' },
    { number: '20M+', label: 'Credits Traded' }
  ];

  const features = [
    { icon: Sparkles, title: 'AI-Powered Analytics', description: 'Advanced carbon footprint analysis with real-time insights and predictive modeling' },
    { icon: Shield, title: 'Verified Solutions', description: 'Certified carbon credits and sustainability frameworks backed by global standards' },
    { icon: Zap, title: 'Rapid Implementation', description: 'Fast-track your Net Zero journey with our streamlined project development' },
    { icon: Globe, title: 'Global Network', description: 'Connected to 13+ regions worldwide with local expertise and partnerships' }
  ];

  const serviceList = [
    'ESG & Sustainability Reporting',
    'Carbon Footprint Management',
    'Net Zero Strategy & Roadmap',
    'Carbon Credit Project Development',
    'Climate Investments & Trading'
  ];

  return (
    <div className="home-page bg-white" data-testid="home-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-[#FAFAFA] overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="inline-flex items-center gap-3 mb-7 fade-in-up">
                <span className="w-8 h-px bg-[var(--green)]" />
                <span className="overline">India's Leading Carbon Credits Consultants</span>
              </div>
              <h1 className="mb-6 fade-in-up" style={{ animationDelay: '0.1s' }}>
                Accelerating Sustainable <span className="text-[#047857]">Growth</span>
              </h1>
              <p className="text-lg md:text-xl text-[#52525B] mb-10 max-w-xl fade-in-up" style={{ animationDelay: '0.2s' }}>
                Empowering businesses with Digitally driven sustainability solutions, verified carbon credits, and measurable climate impact solutions.
              </p>
              <div className="flex flex-wrap gap-4 fade-in-up" style={{ animationDelay: '0.3s' }}>
                <Link to="/services" className="btn-primary" data-testid="hero-explore-btn">
                  <span>Explore Solutions</span>
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <a
                  href="https://calendly.com/shailendra-creduce/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  data-testid="hero-consultation-btn"
                >
                  Schedule Consultation
                </a>
              </div>
            </div>
            <div className="relative fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[var(--green)] z-10" />
              <img src={HERO_IMG} alt="Sustainable growth" className="w-full h-[380px] md:h-[500px] object-cover border border-slate-200 shadow-strong" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#0EA5E9] z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-zinc-200">
          {stats.map((stat, i) => (
            <div key={i} className="p-8 md:p-10 text-center" data-testid={`home-stat-${i}`}>
              <div className="font-display text-4xl md:text-5xl font-extrabold text-[#09090B] mb-2">{stat.number}</div>
              <div className="text-xs md:text-sm text-[#52525B] uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="mb-5">Next-Generation Climate Solutions</h2>
            <p className="text-lg text-[#52525B]">
              Combining cutting-edge technology with deep sustainability expertise to drive measurable environmental impact
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-200">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="p-8 border-b border-r border-zinc-200 group hover:bg-[#FAFAFA] transition-colors duration-200" data-testid={`home-feature-${index}`}>
                  <div className="w-12 h-12 flex items-center justify-center bg-[#047857]/10 mb-6 group-hover:bg-[#047857] transition-colors duration-200">
                    <Icon className="w-6 h-6 text-[#047857] group-hover:text-white transition-colors duration-200" />
                  </div>
                  <h4 className="text-lg font-semibold text-[#09090B] mb-3">{feature.title}</h4>
                  <p className="text-[15px] text-[#52525B] leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <img src={SERVICES_IMG} alt="Renewable Energy" className="w-full h-[400px] md:h-[500px] object-cover border border-slate-200 shadow-soft" />
              <div className="absolute -bottom-5 -left-5 bg-[#0284C7] text-white p-6 hidden md:block shadow-strong">
                <div className="font-display text-3xl font-extrabold">13+</div>
                <div className="text-xs uppercase tracking-wider text-white/80">Regions Worldwide</div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="accent-line mb-6" />
              <h2 className="mb-6">Comprehensive Climate Services</h2>
              <p className="text-lg text-[#52525B] mb-8 leading-relaxed">
                From carbon project development to Net Zero certification, we provide end-to-end sustainability services powered by AI analytics and verified by global standards.
              </p>
              <ul className="space-y-4 mb-10">
                {serviceList.map((service, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#047857] flex-shrink-0" />
                    <span className="text-[#09090B] font-medium">{service}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services" className="btn-primary" data-testid="home-view-services-btn">
                <span>View All Services</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="relative overflow-hidden text-center">
            {/* Background image */}
            <img src={HERO_IMG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
            {/* Rich gradient overlay */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(130deg, rgba(3,28,20,0.97) 0%, rgba(4,67,47,0.92) 45%, rgba(4,120,87,0.80) 100%)' }} />
            {/* Soft teal glow */}
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.20) 0%, transparent 70%)' }} />
            {/* Subtle grid */}
            <div className="absolute inset-0 grid-pattern opacity-[0.07] pointer-events-none" />
            {/* Decorative orbital rings */}
            <div className="absolute -right-24 -bottom-24 w-80 h-80 rounded-full border border-white/10 pointer-events-none" />
            <div className="absolute -right-10 -bottom-10 w-52 h-52 rounded-full border border-[#5EEAD4]/20 pointer-events-none" />
            <div className="absolute -left-20 -top-20 w-72 h-72 rounded-full border border-white/10 pointer-events-none" />

            <div className="relative z-10 max-w-2xl mx-auto px-6 py-20 md:py-28">
              <span className="overline" style={{ color: '#5EEAD4' }}>Start today</span>
              <h2 className="text-white mt-5 mb-6">Ready to Lead the Climate Revolution?</h2>
              <p className="text-lg text-white/80 mb-10 leading-relaxed">
                Join 250+ organizations transforming their environmental impact with our premium sustainability solutions.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-[15px] font-semibold text-[#044e37] bg-white hover:bg-[#5EEAD4] transition-colors duration-200"
                  data-testid="home-cta-start-btn"
                >
                  <span>Start Your Journey</span>
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/projects"
                  className="inline-flex items-center justify-center px-8 py-[15px] font-semibold text-white border border-white/50 hover:bg-white hover:text-[#044e37] transition-colors duration-200 backdrop-blur-sm"
                  data-testid="home-cta-impact-btn"
                >
                  View Our Impact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
